import { useEffect, useState } from 'react';
import { Loader2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FamilyMember, FamilyMemberPayload } from '@/services/account.service';

interface MemberFormDialogProps {
  open: boolean;
  /** Membro em edição; ausente = convite de novo usuário. */
  member: FamilyMember | null;
  isSaving: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: FamilyMemberPayload) => void;
}

const EMPTY_FORM: FamilyMemberPayload = { name: '', email: '' };

export default function MemberFormDialog({
  open,
  member,
  isSaving,
  onOpenChange,
  onSubmit,
}: Readonly<MemberFormDialogProps>) {
  const [form, setForm] = useState<FamilyMemberPayload>(EMPTY_FORM);
  const isInvite = member === null;
  const hasPendingInvite = Boolean(member?.invitation_status);
  // Depois do aceite o e-mail é o login de quem já usa a conta: trocá-lo passaria
  // o acesso a outra pessoa sem aviso. O caminho é excluir e convidar o novo.
  const canEditEmail = isInvite || hasPendingInvite;
  const canSubmit = form.name.trim().length > 0 && form.email.trim().length > 0 && !isSaving;

  useEffect(() => {
    if (open) {
      setForm(member ? { name: member.name, email: member.email } : EMPTY_FORM);
    }
  }, [open, member]);

  const update = (field: keyof FamilyMemberPayload) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: form.name, email: form.email });
  };

  // A senha pertence ao próprio membro: ele a cria pelo link do convite e
  // troca pelo "Esqueceu a senha?" da tela de login — o titular nunca a define.
  let description =
    'Atualize o nome do usuário. A senha é definida pelo próprio usuário — se ele precisar trocar, use "Esqueceu a senha?" na tela de login.';
  if (isInvite) {
    description =
      'O usuário receberá um e-mail com um link para criar a senha e acessar a sua conta.';
  } else if (hasPendingInvite) {
    description =
      'Convite ainda não aceito. Se você alterar o e-mail, um novo convite será enviado para o novo endereço.';
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {isInvite ? 'Convidar usuário' : 'Editar usuário'}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="accountMemberName">Nome completo</Label>
              <Input
                id="accountMemberName"
                value={form.name}
                onChange={update('name')}
                placeholder="Nome completo do usuário"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountMemberEmail">E-mail</Label>
              <Input
                id="accountMemberEmail"
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="usuario@exemplo.com"
                disabled={!canEditEmail}
              />
              {!canEditEmail && (
                <p className="text-sm text-muted-foreground">
                  Este é o login do usuário. Para dar acesso a outra pessoa, exclua este
                  usuário e envie um convite para o novo e-mail.
                </p>
              )}
            </div>

          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isInvite ? 'Enviar convite' : 'Salvar alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
